// Catálogo de `presence_log.type` — ESPEJO de tc_qml/utils/alert_types.py
//
// Al agregar un tipo hay que tocarlo en los DOS lados. Si se agrega sólo en el
// Desktop, acá cae en el default seguro (gris, sin etiqueta) y no rompe nada:
// eso es a propósito, para que un cliente pueda actualizar antes que el Monitor.
//
// REGLA: pintar por SEVERIDAD, nunca comparando números. La condición
// `type > 0 -> rojo` es exactamente lo que hacía que una salida descartada o un
// ingreso de lista negra —que son informativos— salieran como alerta grave.
//
// Desde la 2.0.68 el Desktop publica `alert_label` y `alert_severity` ya
// resueltos en cada fila. Este catálogo es el respaldo para los clientes que
// todavía no actualizaron.

export const TYPE_NORMAL            = 0;
export const TYPE_MANUAL_GATE       = 1;
export const TYPE_TIME_EXCEEDED     = 2;
export const TYPE_NOT_PAID          = 3;
export const TYPE_BLACKLIST         = 4;
export const TYPE_CAPACITY_EXCEEDED = 5;
export const TYPE_REASSIGNED        = 6;
export const TYPE_EXIT_DISCARDED    = 7;
export const TYPE_MONTHLY_DEBT      = 8;
export const TYPE_EXIT_UNIDENTIFIED = 9;
export const TYPE_MONTHLY_OUT_OF_SCHEDULE = 10;
// Desktop 2.0.85+: vehículo de lista blanca que quiere entrar cuando el
// sistema ya lo tiene adentro (opción "No permitir doble ingreso"). Un Desktop
// anterior nunca lo manda, así que para esos clientes no cambia nada.
export const TYPE_WHITELIST_ALREADY_PRESENT = 11;

// Todos los códigos que son una alerta (cualquier severidad). Es la lista del
// filtro "Solo alertas": antes era [1,2,3] y dejaba afuera 4 y 5, que existían
// desde antes de la reasignación.
//
// Va a Firestore como `where("type", "in", ALERT_TYPES)`. Firestore admite
// hasta 30 valores en un `in`, y el SDK instalado (firebase 9.17.2) no pone un
// tope propio: sólo exige que la lista no esté vacía. Si algún día pasa de 30,
// hay que cambiar el filtro, no agregar más códigos acá.
export const ALERT_TYPES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

const LABELS = {
  [TYPE_MANUAL_GATE]:       "Apertura manual",
  [TYPE_TIME_EXCEEDED]:     "Tiempo excedido",
  [TYPE_NOT_PAID]:          "Salida sin pago",
  [TYPE_BLACKLIST]:         "Lista negra",
  [TYPE_CAPACITY_EXCEEDED]: "Capacidad excedida",
  [TYPE_REASSIGNED]:        "Reasignado",
  [TYPE_EXIT_DISCARDED]:    "Salida descartada",
  [TYPE_MONTHLY_DEBT]:      "Abonado con deuda",
  [TYPE_EXIT_UNIDENTIFIED]: "Salida sin identificar",
  [TYPE_MONTHLY_OUT_OF_SCHEDULE]: "Abonado fuera de horario",
  [TYPE_WHITELIST_ALREADY_PRESENT]: "Lista blanca: ya figura adentro",
};

const DESCRIPTIONS = {
  [TYPE_MANUAL_GATE]:       "Apertura manual de barrera",
  [TYPE_TIME_EXCEEDED]:     "Tiempo excedido en el estacionamiento",
  [TYPE_NOT_PAID]:          "Salida sin pago registrado",
  [TYPE_BLACKLIST]:         "Vehículo en lista negra",
  [TYPE_CAPACITY_EXCEEDED]: "Capacidad del estacionamiento excedida",
  [TYPE_REASSIGNED]:        "Movimiento reasignado a un abonado, lista o ticket",
  [TYPE_EXIT_DISCARDED]:    "El vehículo salió sin poder identificarlo y se descartó la tarea",
  [TYPE_MONTHLY_DEBT]:      "El abonado registra deuda al momento de ingresar",
  [TYPE_EXIT_UNIDENTIFIED]: "El vehículo salió y la cámara no pudo identificarlo — pendiente de resolver",
  [TYPE_MONTHLY_OUT_OF_SCHEDULE]:
    "El abonado se movió fuera del horario de su tarifa mensual (Nocturno, Diurno, Fines de semana)",
  [TYPE_WHITELIST_ALREADY_PRESENT]:
    "El vehículo de lista blanca intentó entrar cuando el sistema ya lo tenía adentro",
};

// 'none' = sin marca | 'info' = informativo (azul) | 'warn' = requiere atención (rojo)
const SEVERITY = {
  [TYPE_NORMAL]:            "none",
  [TYPE_MANUAL_GATE]:       "info",
  [TYPE_TIME_EXCEEDED]:     "warn",
  [TYPE_NOT_PAID]:          "warn",
  // 'info' y no 'warn': el ingreso quedó bloqueado, el sistema hizo lo suyo.
  [TYPE_BLACKLIST]:         "info",
  [TYPE_CAPACITY_EXCEEDED]: "warn",
  [TYPE_REASSIGNED]:        "warn",
  // 'info': descartar es una operación legítima y frecuente (un peatón, la
  // lectura de un cartel). Lo que hay que auditar es el VOLUMEN, no cada caso.
  [TYPE_EXIT_DISCARDED]:    "info",
  [TYPE_MONTHLY_DEBT]:      "warn",
  // 'warn' mientras la tarea siga sin resolver: se fue un vehículo sin que el
  // sistema sepa quién era. Al resolverla la fila pasa a 6 o a 7.
  [TYPE_EXIT_UNIDENTIFIED]: "warn",
  // "warn": o el abonado usa un horario que no pagó, o su tarifa está mal
  // cargada. Las dos cosas las tiene que ver el dueño.
  [TYPE_MONTHLY_OUT_OF_SCHEDULE]: "warn",
  // "warn": o hay dos autos con la misma matrícula, o la cámara no vio salir
  // al vehículo y hay que corregir su presencia.
  [TYPE_WHITELIST_ALREADY_PRESENT]: "warn",
};

const ICONS = {
  [TYPE_MANUAL_GATE]:       "back_hand",
  [TYPE_TIME_EXCEEDED]:     "schedule",
  [TYPE_NOT_PAID]:          "attach_money",
  [TYPE_BLACKLIST]:         "block",
  [TYPE_CAPACITY_EXCEEDED]: "groups",
  [TYPE_REASSIGNED]:        "swap_horiz",
  [TYPE_EXIT_DISCARDED]:    "do_not_disturb_on",
  [TYPE_MONTHLY_DEBT]:      "money_off",
  [TYPE_EXIT_UNIDENTIFIED]: "help",
  [TYPE_MONTHLY_OUT_OF_SCHEDULE]: "schedule",
  [TYPE_WHITELIST_ALREADY_PRESENT]: "repeat",
};

function asInt(code) {
  const n = parseInt(code, 10);
  return Number.isNaN(n) ? TYPE_NORMAL : n;
}

/** true si la fila trae una alerta de cualquier severidad. */
export function isAlert(row) {
  return asInt(row?.type) > 0;
}

/**
 * Etiqueta corta. Prefiere la que ya viene resuelta desde el Desktop (2.0.68+),
 * así un tipo nuevo se muestra bien sin deployar el Monitor.
 * Un código desconocido devuelve "" — nunca inventa un nombre.
 */
export function alertLabel(row) {
  if (row?.alert_label) return row.alert_label;
  return LABELS[asInt(row?.type)] || "";
}

/** Texto largo para la ficha. Cae en la etiqueta corta. */
export function alertDescription(row) {
  const c = asInt(row?.type);
  return DESCRIPTIONS[c] || alertLabel(row);
}

/**
 * 'none' | 'info' | 'warn'.
 * Un código que este Monitor no conoce NO alarma: sale 'info' (neutro), nunca
 * rojo. Es lo que permite que el Desktop se distribuya antes que la web.
 */
export function alertSeverity(row) {
  if (row?.alert_severity) return row.alert_severity;
  const c = asInt(row?.type);
  if (c === TYPE_NORMAL) return "none";
  return SEVERITY[c] || "info";
}

/** Ícono de Material Symbols. Los desconocidos usan el genérico. */
export function alertIcon(row) {
  const c = asInt(row?.type);
  if (ICONS[c]) return ICONS[c];
  return alertSeverity(row) === "warn" ? "warning" : "info";
}

/** Color de reactstrap según severidad. */
export function severityColor(severity) {
  if (severity === "warn") return "danger";
  if (severity === "info") return "info";
  return "secondary";
}
